import {
  type Coverage,
  type Distribution,
  type Measure,
  STATS_PERIODS,
  type StatsPeriod,
  coverageRatio,
  rankMeasure,
} from "@trackmark/core";
import styles from "./Statistics.module.css";

/**
 * Statistics (design/README.md §11).
 *
 * Four measures and a hard constraint: **bars only. No pies, no areas, no second accent series.**
 * That is why there is no charting library here — each measure is a list of numbers and a maximum,
 * and a `<div>` with a width is the whole implementation.
 *
 * The rule underneath all four: **nothing unrated is ever plotted as a zero.** A decade you have
 * rated nothing in gets no bar and says so; drawing it at the bottom of the axis would read as a
 * judgement you never made.
 */

export interface StatisticsProps {
  period: StatsPeriod;
  onPeriod: (period: StatsPeriod) => void;
  distribution: Distribution;
  byDecade: Measure[];
  byGenre: Measure[];
  coverage: Coverage;
}

const HISTOGRAM_HEIGHT = 210;

export function Statistics({ period, onPeriod, distribution, byDecade, byGenre, coverage }: StatisticsProps) {
  const ratio = coverageRatio(coverage);

  return (
    <div className={styles.page}>
      <div className={styles.title}>Statistics</div>

      <div className={styles.tabs} data-testid="stats-periods">
        {STATS_PERIODS.map((p) => (
          <button
            key={p.period}
            type="button"
            className={styles.tab}
            data-active={p.period === period}
            onClick={() => onPeriod(p.period)}
          >
            {p.label}
          </button>
        ))}
      </div>

      {/* 1. Rating distribution */}
      <section className={styles.section}>
        <div className={styles.sectionHead}>
          <h2 className={styles.sectionTitle}>Rating distribution</h2>
          <div className={styles.note} data-testid="dist-note">
            {distribution.total === 0
              ? "NOTHING RATED IN THIS PERIOD"
              : `MEAN ${distribution.mean!.toFixed(2)} · MEDIAN ${distribution.median} · MODE ${distribution.mode}`}
          </div>
        </div>

        <div className={styles.histogram} style={{ height: HISTOGRAM_HEIGHT }} data-testid="histogram">
          {distribution.counts.map((count, i) => {
            const score = i + 1;
            return (
              <div key={score} className={styles.column}>
                <div className={styles.columnCount}>{count === 0 ? "" : count.toLocaleString()}</div>
                <div
                  className={styles.bar}
                  // The strip's own fill language at poster size: 8–10 in ink, everything below
                  // in the recessive grey, so the shape of what you actually like is readable
                  // from across the room.
                  data-high={score >= 8 || undefined}
                  data-testid="histogram-bar"
                  style={{
                    height: distribution.peak === 0 ? 0 : `${(count / distribution.peak) * (HISTOGRAM_HEIGHT - 40)}px`,
                  }}
                />
                <div className={styles.columnLabel} data-mode={score === distribution.mode || undefined}>
                  {score}
                </div>
              </div>
            );
          })}
        </div>
      </section>

      <BarMeasure title="Average score by decade" measures={byDecade} testId="by-decade" />
      <BarMeasure title="Average score by genre" measures={byGenre} testId="by-genre" />

      {/* 4. Collection coverage */}
      <section className={styles.section}>
        <h2 className={styles.sectionTitle}>Collection coverage</h2>
        <div className={styles.coverage} data-testid="coverage">
          <div className={styles.coveragePercent}>{Math.round(ratio * 100)}%</div>
          <div className={styles.coverageBody}>
            <div className={styles.coverageTrack}>
              <div className={styles.coverageFill} style={{ width: `${ratio * 100}%` }} />
            </div>
            <div className={styles.coverageCounts}>
              {coverage.rated.toLocaleString()} RATED / {coverage.inLibrary.toLocaleString()} IN LIBRARY /{" "}
              {coverage.unrated.toLocaleString()} STILL UNRATED / {coverage.albumsComplete.toLocaleString()} ALBUMS
              COMPLETE OF {coverage.albumsTotal.toLocaleString()}
            </div>
            {/* Coverage is a statement about the collection, so it does not move with the period
                selector. Saying so is cheaper than someone wondering why. */}
            <div className={styles.coverageNote}>THE WHOLE COLLECTION, WHICHEVER PERIOD IS SELECTED.</div>
          </div>
        </div>
      </section>
    </div>
  );
}

/**
 * A horizontal bar measure: value and n on every row.
 *
 * `n` is not decoration. An 8.6 average over three songs and an 8.6 over ninety are different
 * claims, and without the count the chart presents them as the same one.
 */
function BarMeasure({ title, measures, testId }: { title: string; measures: Measure[]; testId: string }) {
  const ranked = rankMeasure(measures);

  return (
    <section className={styles.section}>
      <h2 className={styles.sectionTitle}>{title}</h2>
      {ranked.length === 0 ? (
        <div className={styles.empty}>NOTHING TO MEASURE YET.</div>
      ) : (
        <div className={styles.bars} data-testid={testId}>
          {ranked.map((m) => (
            <div key={m.key} className={styles.barRow} data-testid={`${testId}-row`}>
              <div className={styles.barLabel} data-none={m.key === "none" || undefined}>
                {m.label}
              </div>
              <div className={styles.barTrack}>
                <div
                  className={styles.barFill}
                  data-empty={m.average === null || undefined}
                  /*
                   * Scaled to the RATING SCALE, not to the tallest bar. Averages cluster in the
                   * sevens and eights, so peak-scaling stretches a 1.2-point spread across the
                   * whole track and makes a nine-tenths difference look like everything. Against
                   * 0–10 a bar length means "this is what an eight looks like", which is a fact
                   * about the score rather than about the other rows.
                   */
                  style={{ width: m.average === null ? 0 : `${(m.average / 10) * 100}%` }}
                />
              </div>
              <div className={styles.barValue}>
                {/* Never 0.0: a bucket with nothing rated in it has no average, and drawing one
                    would read as a judgement nobody made. */}
                {m.average === null ? "—" : m.average.toFixed(2)}
              </div>
              <div className={styles.barCount}>{m.count === 0 ? "NONE RATED" : `n ${m.count.toLocaleString()}`}</div>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}
